/**
 * Hooks for the Auvik tenant mapping screens.
 *
 * Reads auvik_tenant_map under staff RLS. Mapping changes use the checked
 * administrator review RPC with the revision inspected in the form.
 * Refreshing the inventory from Auvik is a Trigger.dev task queued through
 * the `trigger-task` Edge Function (lib/triggerTask.ts).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { invokeTriggerTask } from '../lib/triggerTask';

export interface AuvikTenantRow {
  id: string;
  auvik_tenant_id: string;
  auvik_tenant_name: string | null;
  auvik_tenant_attributes: Record<string, unknown> | null;
  customer_id: string | null;
  location_id: string | null;
  mapped_by_user_id: string | null;
  mapped_at: string | null;
  created_at: string;
  updated_at: string;
  location?: { id: string; name: string } | null;
}

const TENANT_COLUMNS = `id, auvik_tenant_id, auvik_tenant_name, auvik_tenant_attributes,
  customer_id, location_id, mapped_by_user_id, mapped_at,
  created_at, updated_at, location:locations(id, name)`;

export function providerAssignmentError(error: { code?: string; message: string }) {
  return new Error(error.code === 'PT409' || error.code === '40001'
    ? 'This provider assignment changed after review. Close and reopen the form, then check the current ownership before saving.'
    : error.message);
}

export function useAuvikTenants() {
  return useQuery({
    queryKey: ['auvik-tenants'],
    queryFn: async (): Promise<AuvikTenantRow[]> => {
      const { data, error } = await supabase
        .from('auvik_tenant_map')
        .select(TENANT_COLUMNS)
        .order('auvik_tenant_name', { ascending: true });
      if (error) throw providerAssignmentError(error);
      return (data ?? []) as unknown as AuvikTenantRow[];
    },
    refetchInterval: 30_000,
  });
}

export interface DiscoveredAuvikTenant { id: string; name: string; ignored?: boolean }
/** Copied provider metadata remains available before collector activation. */
export function useDiscoveredAuvikTenants(enabled: boolean) {
  return useQuery({
    queryKey: ['mission-control-auvik-discovery'], enabled,
    queryFn: async (): Promise<DiscoveredAuvikTenant[]> => {
      const { data, error } = await supabase.rpc('get_mission_control_provider_catalog', { p_provider_kind: 'auvik' });
      if (error) throw providerAssignmentError(error);
      const rows = data as unknown as { provider_id: string; name: string; ignored: boolean }[];
      return Array.isArray(rows) ? rows.filter((row) => typeof row?.provider_id === 'string' && row.provider_id.length > 0)
        .map((row) => ({ id: row.provider_id, name: typeof row.name === 'string' ? row.name : row.provider_id, ignored: row.ignored === true })) : [];
    }, staleTime: 60_000,
  });
}

/** Tenants under a single customer — used by the customer-detail Auvik tab. */
export function useAuvikTenantsForCustomer(customerId: string | null) {
  return useQuery({
    queryKey: ['auvik-tenants', customerId],
    enabled: !!customerId,
    queryFn: async (): Promise<AuvikTenantRow[]> => {
      const { data, error } = await supabase
        .from('auvik_tenant_map')
        .select(TENANT_COLUMNS)
        .eq('customer_id', customerId!)
        .order('auvik_tenant_name', { ascending: true });
      if (error) throw providerAssignmentError(error);
      return (data ?? []) as unknown as AuvikTenantRow[];
    },
  });
}

interface MapTenantParams {
  id: string;
  customer_id: string;
  location_id: string;
  expected_updated_at: string;
  reason: string;
}

/**
 * The server derives the actor from the current live Portal session.
 */
export function useMapAuvikTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: MapTenantParams) => {
      const { error } = await supabase.rpc('assign_mission_control_provider_location', {
        p_provider_kind: 'auvik', p_provider_row_id: params.id,
        p_expected_updated_at: params.expected_updated_at,
        p_customer_id: params.customer_id, p_location_id: params.location_id,
        p_reason: params.reason,
      });
      if (error) throw providerAssignmentError(error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auvik-tenants'] });
      qc.invalidateQueries({ queryKey: ['admin-platform-metrics'] });
    },
    onError: () => { qc.invalidateQueries({ queryKey: ['auvik-tenants'] }); },
  });
}

export function useRegisterDiscoveredAuvikTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { provider_id: string; customer_id: string; location_id: string; reason: string }) => {
      const { error } = await supabase.rpc('register_mission_control_provider_location', {
        p_provider_kind: 'auvik', p_provider_id: params.provider_id,
        p_customer_id: params.customer_id, p_location_id: params.location_id, p_reason: params.reason,
      });
      if (error) throw providerAssignmentError(error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auvik-tenants'] });
      qc.invalidateQueries({ queryKey: ['admin-platform-metrics'] });
    },
    onError: () => { qc.invalidateQueries({ queryKey: ['auvik-tenants'] }); },
  });
}

export function useUnmapAuvikTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; expected_updated_at: string; reason: string }) => {
      const { error } = await supabase.rpc('assign_mission_control_provider_location', {
        p_provider_kind: 'auvik', p_provider_row_id: params.id,
        p_expected_updated_at: params.expected_updated_at,
        // SQL requires explicit NULL for detachment; generated function types
        // omit argument nullability, so retain NULL on the JSON wire.
        p_customer_id: null as unknown as string,
        p_location_id: null as unknown as string, p_reason: params.reason,
      });
      if (error) throw providerAssignmentError(error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auvik-tenants'] });
      qc.invalidateQueries({ queryKey: ['admin-platform-metrics'] });
    },
    onError: () => { qc.invalidateQueries({ queryKey: ['auvik-tenants'] }); },
  });
}

/**
 * Queues the Trigger.dev `auvik-sync-tenant-inventory` task, which refreshes
 * auvik_tenant_map from the Auvik API. Rejects with a TriggerTaskError when
 * the job service is unavailable so the caller can show it.
 */
export function useSyncAuvikInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => invokeTriggerTask('auvik-sync-tenant-inventory', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auvik-tenants'] });
    },
  });
}

/** Locations under a customer for the location dropdown in the mapping form. */
export interface LocationLite {
  id: string;
  customer_id: string;
  name: string;
}

export function useLocationsForCustomer(customerId: string | null) {
  return useQuery({
    queryKey: ['locations-lite', customerId],
    enabled: !!customerId,
    queryFn: async (): Promise<LocationLite[]> => {
      const { data, error } = await supabase
        .from('locations')
        .select('id, customer_id, name')
        .eq('customer_id', customerId!)
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;
      return (data ?? []) as LocationLite[];
    },
  });
}
